import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
// import { api, ApiClientError } from "@/lib/api"; // Uncomment when backend is ready

// Define the Browser type
interface Browser {
  id: string;
  browser_name: string;
  ncii_instances: number;
  type: string;
  region: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  contacts: string[]; // Array of email addresses
}

const PAGE_SIZE = 50;

export function BrowserList() {
  const { logout } = useAuth();
  const [browsers, setBrowsers] = useState<Browser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadBrowsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call
      // const data = await api.browsers.list(PAGE_SIZE, currentPage);
      // setBrowsers(data);
      // setHasMore(data.length === PAGE_SIZE);
      // setTotalCount(data.totalCount); // Assuming API returns total count

      // Mock data for now
      const mockBrowsers: Browser[] = [
        {
          id: "1",
          browser_name: "Google Chrome",
          ncii_instances: 1234,
          type: "Chromium-based",
          region: "Global",
          active_urls: 230,
          removed_urls: 1004,
          takedown_rate: 0.81,
          avg_response_time: 2.5,
          contacts: ["abuse@google.com", "safebrowsing@google.com"],
        },
        {
          id: "2",
          browser_name: "Safari",
          ncii_instances: 892,
          type: "WebKit-based",
          region: "Global",
          active_urls: 166,
          removed_urls: 726,
          takedown_rate: 0.81,
          avg_response_time: 2.8,
          contacts: ["abuse@apple.com", "websafety@apple.com"],
        },
        {
          id: "3",
          browser_name: "Mozilla Firefox",
          ncii_instances: 567,
          type: "Gecko-based",
          region: "Global",
          active_urls: 106,
          removed_urls: 461,
          takedown_rate: 0.81,
          avg_response_time: 2.3,
          contacts: ["abuse@mozilla.org"],
        },
        {
          id: "4",
          browser_name: "Microsoft Edge",
          ncii_instances: 734,
          type: "Chromium-based",
          region: "Global",
          active_urls: 137,
          removed_urls: 597,
          takedown_rate: 0.81,
          avg_response_time: 2.6,
          contacts: ["abuse@microsoft.com", "edge-security@microsoft.com"],
        },
        {
          id: "5",
          browser_name: "Opera",
          ncii_instances: 298,
          type: "Chromium-based",
          region: "Global",
          active_urls: 56,
          removed_urls: 242,
          takedown_rate: 0.81,
          avg_response_time: 2.9,
          contacts: ["abuse@opera.com"],
        },
        {
          id: "6",
          browser_name: "Brave",
          ncii_instances: 189,
          type: "Chromium-based",
          region: "Global",
          active_urls: 35,
          removed_urls: 154,
          takedown_rate: 0.81,
          avg_response_time: 2.4,
          contacts: ["abuse@brave.com", "security@brave.com"],
        },
        {
          id: "7",
          browser_name: "Vivaldi",
          ncii_instances: 134,
          type: "Chromium-based",
          region: "Global",
          active_urls: 25,
          removed_urls: 109,
          takedown_rate: 0.81,
          avg_response_time: 2.7,
          contacts: ["abuse@vivaldi.com"],
        },
        {
          id: "8",
          browser_name: "DuckDuckGo Browser",
          ncii_instances: 156,
          type: "Privacy-focused",
          region: "Global",
          active_urls: 29,
          removed_urls: 127,
          takedown_rate: 0.81,
          avg_response_time: 2.2,
          contacts: ["abuse@duckduckgo.com"],
        },
        {
          id: "9",
          browser_name: "Samsung Internet",
          ncii_instances: 412,
          type: "Chromium-based",
          region: "Global",
          active_urls: 77,
          removed_urls: 335,
          takedown_rate: 0.81,
          avg_response_time: 3.0,
          contacts: ["abuse@samsung.com", "browser-security@samsung.com"],
        },
        {
          id: "10",
          browser_name: "UC Browser",
          ncii_instances: 523,
          type: "Chromium-based",
          region: "Asia",
          active_urls: 97,
          removed_urls: 426,
          takedown_rate: 0.81,
          avg_response_time: 3.5,
          contacts: ["abuse@ucweb.com"],
        },
      ];
      setBrowsers(mockBrowsers);
      setHasMore(false);
      setTotalCount(mockBrowsers.length); // For mock data, total equals current data length
    } catch (err) {
      // if (err instanceof ApiClientError) {
      //   if (err.status === 401) {
      //     logout();
      //   } else {
      //     setError(err.detail || "Failed to load browsers");
      //   }
      // } else {
      //   setError("Failed to load browsers");
      // }
      setError("Failed to load browsers");
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadBrowsers();
  }, [loadBrowsers]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Browsers table
  const columns: ColumnDef<Browser>[] = [
    {
      key: "browser_name",
      header: "Browser Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.browser_name}
        </div>
      ),
      sortValue: (item) => item.browser_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region}</div>
      ),
      sortValue: (item) => item.region,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts.join(", ")}
        </div>
      ),
      sortValue: (item) => item.contacts.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to browser details or open modal
            console.log("View browser:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];

  // Handle new browser creation
  const handleNewBrowser = () => {
    // TODO: Implement new browser modal or navigation
    console.log("Add new browser");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export browsers");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewBrowser}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Browser
      </Button>
    </>
  );

  return (
    <DataTable
      title="Browsers"
      subtitle="Track browser vendors and content filtering"
      columns={columns}
      data={browsers}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search browsers..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="browsers"
    />
  );
}



